/**
 * GoogleAnalytics – GA4 med GDPR-consent.
 *
 * Beteende:
 * - Läser samtyckesnivån från localStorage-nyckeln som CookieConsent-bannern sätter.
 * - Om level === "all": injicerar gtag.js-scriptet en enda gång och skickar page_view vid varje route-byte.
 * - Om level !== "all": laddar INGET script (inga cookies, inga anrop → GDPR-korrekt).
 * - Lyssnar på "cookieconsent:allow-analytics"-eventet så samtycke aktiveras direkt när användaren klickar
 *   "Tillåt alla" utan att sidan behöver laddas om.
 * - Ingen tracking sker om REACT_APP_GA_MEASUREMENT_ID saknas (t.ex. i dev/preview utan riktig property).
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const CONSENT_KEY = "bygglovkollen_cookies_v1";
const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw).level;
  } catch {
    return null;
  }
}

function injectGtag(id) {
  if (window.__gaLoaded) return;
  window.__gaLoaded = true;

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  gtag("js", new Date());
  gtag("config", id, {
    anonymize_ip: true, // Extra GDPR-skydd (IP-adressen anonymiseras)
    send_page_view: false, // Vi skickar manuellt vid route-byte istället
  });
}

function sendPageView(path, title) {
  if (typeof window.gtag !== "function") return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
}

export default function GoogleAnalytics() {
  const location = useLocation();
  const enabledRef = useRef(false);

  // Steg 1: aktivera vid mount om consent redan finns (t.ex. returnerande besökare)
  useEffect(() => {
    if (!GA_ID) return;
    if (readConsent() === "all") {
      injectGtag(GA_ID);
      enabledRef.current = true;
      sendPageView(location.pathname + location.search, document.title);
    }
    // Steg 2: aktivera direkt när användaren klickar "Tillåt alla" i cookie-bannern
    const handler = () => {
      if (!GA_ID) return;
      injectGtag(GA_ID);
      enabledRef.current = true;
      sendPageView(window.location.pathname + window.location.search, document.title);
    };
    window.addEventListener("cookieconsent:allow-analytics", handler);
    return () => window.removeEventListener("cookieconsent:allow-analytics", handler);
  }, []);

  // Steg 3: skicka page_view vid varje route-byte (bara om enabled)
  useEffect(() => {
    if (!enabledRef.current) return;
    // Litet delay så React Helmet hunnit uppdatera document.title
    const t = setTimeout(() => {
      sendPageView(location.pathname + location.search, document.title);
    }, 100);
    return () => clearTimeout(t);
  }, [location]);

  return null;
}
